import gql from 'graphql-tag';

import {
  instantPromotionFields,
  potentialPromotionsFragment
} from './potential-promotions-fragment';
import {
  markdownPriceDataFragment,
  pdpTdsDataFields,
  topRecommendedProductDataFields
} from './product-fragments';
import { stockFields } from './product-list-queries';

const recommendationFields =
  process.env.__FF_TREADWELL_AI_QUERY_UPDATES__ === 'true'
    ? gql`
  fragment recommendationFields on RecommendedArticleData {
    tireLifeYears
    tireLifeMonths
    totalCost
    totalCostOfOwnershipRating
    code
    milesPerDollar
    stopping {
      wetRating
      wetDistance
      wetWornDistance
      rollingResistanceRating
      dryDistance
      dryRating
      winterRating
    }
    rank
    rideRating {
      handling
      quiet
      comfort
      wetHandling
    }
    mileage {
      warranty
      min
      median
    }
    topRecommendedProductData {
      frontProduct {
        ...topRecommendedProductDataFields
      }
      rearProduct {
        ...topRecommendedProductDataFields
      }
    }
    treadwellSessionResponseCacheKey
    winter {
      snowAccelerationRating,
      snowBrakeFeet,
      snowBrakeRating,
      snowHandlingRating,
      snowLateralGRating
    }
  }
  ${topRecommendedProductDataFields}
`
    : gql`
  fragment recommendationFields on RecommendedArticleData {
    tireLifeYears
    tireLifeMonths
    totalCost
    totalCostOfOwnershipRating
    code
    milesPerDollar
    stopping {
      wetRating
      wetDistance
      wetWornDistance
      rollingResistanceRating
      dryDistance
      dryRating
      winterRating
    }
    rank
    rideRating {
      handling
      quiet
      comfort
      wetHandling
    }
    topRecommendedProductData {
      frontProduct {
        ...topRecommendedProductDataFields
      }
      rearProduct {
        ...topRecommendedProductDataFields
      }
    }
    mileage {
      warranty
      min
      median
    }
    winter {
      snowAccelerationRating,
      snowBrakeFeet,
      snowBrakeRating,
      snowHandlingRating,
      snowLateralGRating
    }
  }
  ${topRecommendedProductDataFields}
`;

const compareFleetPreferredFields =
  process.env.__FF_FLEET_ACCOUNT__ === 'true'
    ? gql`
        fragment compareFleetPreferredFields on ProductData {
          isFleetPreferred
          addToCartDisabled
        }
      `
    : gql`
        fragment compareFleetPreferredFields on ProductData {
          addToCartDisabled
        }
      `;

const compareByCodesFields = gql`
  fragment compareByCodesFields on ProductData {
    ...compareFleetPreferredFields
    aggregates {
      allVehiclesReviewCount
    }
    articleServiceType
    articleStatus
    articleStatusLabel
    averageRating
    baseprice {
      formattedValue
      value
    }
    brand
    brandLogo {
      url
    }
    code
    coreMarketingCode
    description
    freeShipping
    gbb
    icons {
      altText
    }
    images {
      url
      altText
      format
    }
    isCoreProduct
    isExclusive
    isVirtualAssortmentParent
    loadCapacity
      mapPrice {
      currencyIso
      formattedValue
      priceType
      value
    }
    loadIndex
    loadRange
    mapDisplayRule {
      messageDisplay
      mapPriceDisplay
      message
    }
    mapRuleSatisfied
    manufacturerMileageWarranty
    markdownPriceData {
      ...markdownPriceDataFragment
    }
    maxAirPressure
    mileageWarranty
    name
    price {
      formattedValue
      value
    }
    priceRange {
      minPrice {
        value
      }
      maxPrice {
        value
      }
    }
    productType
    potentialPromotions {
      ...potentialPromotionsFragment
    }
    reviewSummaryData {
      recommendCount
      totalCount
      totalRating
    }
    recommendationData {
      ...recommendationFields
    }
    rimWidth
    sideWallDescriptionName
    size
    speedRating
    stock {
      ...stockFields
    }
    tdsData {
      ...pdpTdsDataFields,
      topRecommendedProductData {
        frontProduct {
          ...topRecommendedProductDataFields
        }
        rearProduct {
          ...topRecommendedProductDataFields
        }
      }
    }
    tempGrade
    tireAspectRatio
    tireCrossSection
    tireDiameter
    tireWheelRimDiameter
    tractionGrade
    treadDepth
    treadGrade
    treadWidth
    grossWeight
    unitName
    url
    wheelRimDiameter
    widthRange
  }
  ${markdownPriceDataFragment}
  ${recommendationFields}
  ${potentialPromotionsFragment}
  ${stockFields}
  ${instantPromotionFields}
  ${pdpTdsDataFields}
  ${compareFleetPreferredFields}
`;

export const COMPARE_STAGGERED_PRODUCT_QUERY = gql`
  query compareProductData(
    $nearByStoreCodes: [String]
    $storeCode: String!
    $treadwellRequest: TreadwellRequest
    $vehicleInfo: VehicleInput
    $zipCode: String
    $productCodes: [ProductCode]!
    $compareSelectorProductCodes: [CompareSelectorInput]
  ) {
    product {
      averageRatings(productCodes: $productCodes) {
        overallRating
        averageRatings {
          value
          key
        }
        recommendedPercentage
        totalReviews
      }

      byCodes(
        nearByStoreCodes: $nearByStoreCodes
        treadwellRequest: $treadwellRequest
        vehicleInfo: $vehicleInfo
        productCodes: $productCodes
        storeCode: $storeCode
        compareSelectorProductCodes: $compareSelectorProductCodes
      ) {
        ...compareByCodesFields
        freeShipping
        isOtherVehiclesOE
        rearProduct {
          ...compareFleetPreferredFields
          articleStatus
          code
          brand
          brandLogo {
            url
          }
          images {
            url
            altText
            format
          }
          gbb
          grossWeight
          mapDisplayRule {
            mapPriceDisplay
            message
            messageDisplay
          }
          loadIndex
          loadRange
          mapPrice {
            currencyIso
            formattedValue
            priceType
            value
          }
          mapRuleSatisfied
          markdownPriceData {
            ...markdownPriceDataFragment
          }
          maxAirPressure
          potentialPromotions {
            ...potentialPromotionsFragment
          }
          mileageWarranty
          productType
          price {
            formattedValue
            value
          }
          rimWidth
          sideWallDescriptionName
          size
          speedRating
          stock {
            ...stockFields
          }
          tempGrade
          tireAspectRatio
          tireCrossSection
          tireDiameter
          tireWheelRimDiameter
          tractionGrade
          treadDepth
          treadGrade
          unitName
          widthRange
        }
      }
    }
    recommendationQueries {
      weatherDetails(zipCode: $zipCode) {
        winterDaysPerYear
        precipitationDaysPerYear
      }
    }
  }
  ${markdownPriceDataFragment}
  ${compareByCodesFields}
  ${potentialPromotionsFragment}
  ${stockFields}
`;

export const COMPARE_STANDARD_PRODUCT_QUERY = gql`
  query compareProductData(
    $nearByStoreCodes: [String]
    $storeCode: String!
    $treadwellRequest: TreadwellRequest
    $vehicleInfo: VehicleInput
    $zipCode: String
    $productCodes: [String]!
    $compareSelectorProductCodes: [CompareSelectorInput]
  ) {
    product {
      averageRatingsStandard(productCodes: $productCodes) {
        overallRating
        averageRatings {
          value
          key
        }
        recommendedPercentage
        totalReviews
      }

      byCodesStandard(
        nearByStoreCodes: $nearByStoreCodes
        treadwellRequest: $treadwellRequest
        vehicleInfo: $vehicleInfo
        productCodes: $productCodes
        storeCode: $storeCode
        compareSelectorProductCodes: $compareSelectorProductCodes
      ) {
        ...compareByCodesFields
        freeShipping
        isOtherVehiclesOE
      }
    }
    recommendationQueries {
      weatherDetails(zipCode: $zipCode) {
        winterDaysPerYear
        precipitationDaysPerYear
      }
    }
  }
  ${compareByCodesFields}
`;
